import { GraphQLClient } from 'graphql-request';
import { print } from 'graphql/language';
import { useCartStore } from '@/stores/cart';

// Import GraphQL operations from the new folder structure
import createCartMutation from '@/graphql/shopify/cart/createCartMutation.gql';
import addLinesMutation from '@/graphql/shopify/cart/addLinesMutation.gql';
import addDiscountCodeMutation from '@/graphql/shopify/cart/addDiscountCodeMutation.gql';
import getCartQuery from '@/graphql/shopify/cart/getCartQuery.gql';
import cartAttributesUpdate from '@/graphql/shopify/cart/updateCartAttributesMutation.gql';
import updateLinesMutation from '@/graphql/shopify/cart/updateLinesMutation.gql';
import updateBuyerIdentityMutation from '@/graphql/shopify/cart/updateBuyerIdentityMutation.gql';

export default defineNuxtPlugin((nuxtApp) => {
  const {
    $config,
    $i18n,
    $i18n: { locale, locales },
  } = nuxtApp;

  const { shopifyApiVersion } = $config.public;

  let client = null;

  const createClient = (locale) => {
    const { shopifyApiAccessToken, shopifyApiDomain } = locales.value?.find((l) => l.code === locale) || {};

    if (!shopifyApiAccessToken || !shopifyApiDomain) {
      throw new Error('Shopify API credentials are missing for the current locale. Please check your configuration.');
    }

    const endPoint = `https://${shopifyApiDomain}/api/${shopifyApiVersion}/graphql`;
    client = new GraphQLClient(endPoint, {
      headers: {
        'X-Shopify-Storefront-Access-Token': shopifyApiAccessToken,
        'accept-language': locale.value,
        Accept: 'application/json',
      },
    });
  };

  createClient(locale.value);

  $i18n.onBeforeLanguageSwitch = (oldLocale, newLocale) => {
    createClient(newLocale);
    nuxtApp.$shopifyRefreshClient(newLocale);
  };

  $i18n.onLanguageSwitched = () => {
    const cartStore = useCartStore();
    cartStore.resetCart();
  };

  const resolveCountry = () => {
    const map = {
      'ch-fr': 'CH',
      'ch-de': 'CH',
      de: 'DE',
      nl: 'NL',
      fr: 'FR',
      en: 'GB',
    };

    return map[locale.value] || locale.value.toUpperCase();
  };

  const resolveLocale = () => {
    let currentLocale = locale.value;

    const map = {
      'ch-fr': 'fr',
      'ch-de': 'de',
    };

    if (map[locale.value]) {
      currentLocale = map[locale.value];
    }

    return currentLocale.toUpperCase();
  };

  const updateBuyerIdentity = async (cartId, buyerIdentity) => {
    try {
      const response = await client.request(print(updateBuyerIdentityMutation), {
        id: cartId,
        buyerIdentity,
        lang: resolveLocale(),
      });

      if (response.cartBuyerIdentityUpdate.userErrors.length) {
        return Promise.reject(response.cartBuyerIdentityUpdate.userErrors);
      }

      return response.cartBuyerIdentityUpdate.cart;
    } catch (error) {
      console.warn('oh no', error);
      return Promise.reject(error);
    }
  };

  const createCart = async (lang) => {
    try {
      const response = await client.request(print(createCartMutation), { input: {}, lang });

      if (response.cartCreate.userErrors.length) {
        return Promise.reject(response.cartCreate.userErrors);
      }

      return response.cartCreate.cart;
    } catch (error) {
      console.warn('oh no', error);
      return Promise.reject(error);
    }
  };

  const getCart = async (cartId) => {
    try {
      const response = await client.request(print(getCartQuery), { id: cartId, lang: resolveLocale() });

      return response.cart;
    } catch (error) {
      console.warn('oh no', error);
      return Promise.reject(error);
    }
  };

  const addLines = async (cartId, lines) => {
    try {
      const response = await client.request(print(addLinesMutation), { id: cartId, lines, lang: resolveLocale() });

      if (response.cartLinesAdd.userErrors.length) {
        return Promise.reject(response.cartLinesAdd.userErrors);
      }

      return response.cartLinesAdd.cart;
    } catch (error) {
      console.warn('oh no', error);
      return Promise.reject(error);
    }
  };

  const updateLines = async (cartId, lines) => {
    try {
      const response = await client.request(print(updateLinesMutation), { id: cartId, lines, lang: resolveLocale() });

      if (response.cartLinesUpdate.userErrors.length) {
        return Promise.reject(response.cartLinesUpdate);
      }

      return response.cartLinesUpdate.cart;
    } catch (error) {
      console.warn('oh no', error);
      return Promise.reject(error);
    }
  };

  const addDiscount = async (cartId, discountCodes) => {
    try {
      const response = await client.request(print(addDiscountCodeMutation), {
        id: cartId,
        discountCodes,
        lang: resolveLocale(),
      });

      if (response.cartDiscountCodesUpdate.userErrors.length) {
        return Promise.reject(response.cartDiscountCodesUpdate.userErrors);
      }

      return response.cartDiscountCodesUpdate.cart;
    } catch (error) {
      console.warn('oh no', error);
      return Promise.reject(error);
    }
  };

  const updateAttributes = async (cartId, attributes = []) => {
    try {
      const response = await client.request(print(cartAttributesUpdate), {
        id: cartId,
        attributes,
        lang: resolveLocale(),
      });

      if (response.cartAttributesUpdate.userErrors.length) {
        return Promise.reject(response.cartAttributesUpdate.userErrors);
      }

      return response.cartAttributesUpdate.cart;
    } catch (error) {
      console.warn('oh no', error);
      return Promise.reject(error);
    }
  };

  return {
    provide: {
      shopifyClient: {
        addLines,
        createCart,
        getCart,
        addDiscount,
        updateAttributes,
        updateLines,
        updateBuyerIdentity,
        resolveLocale,
        resolveCountry,
      },
    },
  };
});
